/**
 * Money conversion lives in exactly ONE file.
 *
 * All internal state is integer pence. The only reason a decimal ever exists is
 * that SumUp's *online* checkout API takes major units (`amount: 3.00`) while
 * its *reader* API takes minor units (`value: 300, minor_unit: 2`). Getting that
 * backwards charges someone 300 pounds instead of 3. See money.test.ts.
 */
/** Integer pence -> major units for SumUp's online checkout `amount` field. */
export function penceToMajorUnits(pence) {
    assertPence(pence);
    // Divide, don't multiply: 300 / 100 is exact in binary floating point for all
    // values we can represent, and we round to 2dp to kill any residue anyway.
    return Math.round(pence) / 100;
}
/** Major units (as returned by SumUp) -> integer pence. */
export function majorUnitsToPence(amount) {
    if (!Number.isFinite(amount))
        throw new RangeError(`amount is not finite: ${amount}`);
    const pence = Math.round(amount * 100);
    assertPence(pence);
    return pence;
}
/** Integer pence for SumUp's *reader* API shape. Not used by hosted checkout. */
export function penceToMinorUnits(pence) {
    assertPence(pence);
    return { value: pence, minor_unit: 2 };
}
/** "300" -> "£3.00". Display only; never feed this back into arithmetic. */
export function formatPence(pence, currency = 'GBP') {
    assertPence(pence);
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
    }).format(pence / 100);
}
export function assertPence(pence) {
    if (!Number.isInteger(pence)) {
        throw new RangeError(`money must be integer pence, got ${pence}`);
    }
    if (pence < 0)
        throw new RangeError(`money must not be negative, got ${pence}`);
    if (pence > 100_000_00)
        throw new RangeError(`money implausibly large: ${pence}`);
}
/**
 * For a figure that can legitimately be negative -- a night's net after
 * refunds, most obviously, where refunding more than was taken is unusual but
 * not impossible.
 *
 * `formatPence` deliberately throws on a negative, and that guard is worth
 * keeping: nearly every amount in this app (a fee, a ticket, a refund) is a
 * quantity that cannot sensibly go below zero, and one that has is a bug worth
 * hearing about. But a *display* function throwing takes React's whole tree
 * down with it -- the door tab went blank mid-quiz because the takings summed
 * to -£3.00 -- so a value that is allowed to be negative gets its own formatter
 * rather than a weakened guard.
 */
export function formatPenceSigned(pence, currency = 'GBP') {
    if (!Number.isInteger(pence)) {
        throw new RangeError(`money must be integer pence, got ${pence}`);
    }
    const formatted = formatPence(Math.abs(pence), currency);
    return pence < 0 ? `-${formatted}` : formatted;
}
//# sourceMappingURL=money.js.map